import { useState, useEffect } from 'react';
import api from '../services/api';
import { FileText, Download, CheckCircle, Warning } from '@phosphor-icons/react';

export default function TenantDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tenant/documents')
      .then(res => setDocs(res.data))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  const download = async (doc) => {
    try {
      const res = await api.get(`/tenant/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'dokument.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download fehlgeschlagen');
    }
  };

  const fmtEur = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6 max-w-3xl mx-auto py-8 px-4" data-testid="tenant-documents-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-slate-900">Meine Dokumente</h1>
        <p className="text-slate-500 mt-1 text-sm">Nebenkostenabrechnungen, Verträge und weitere Unterlagen</p>
      </div>

      {docs.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center" data-testid="tenant-documents-empty">
          <FileText weight="duotone" className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Noch keine Dokumente vorhanden.</p>
          <p className="text-xs text-slate-400 mt-2">Sobald Ihre Hausverwaltung eine Nebenkostenabrechnung freigibt, erscheint sie hier.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {docs.map(d => (
            <div key={d.id} className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3" data-testid={`tenant-doc-${d.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${d.ergebnis === 'guthaben' ? 'bg-emerald-100' : d.ergebnis === 'nachzahlung' ? 'bg-red-100' : 'bg-slate-100'}`}>
                  {d.ergebnis === 'guthaben' ? <CheckCircle weight="fill" className="w-5 h-5 text-emerald-600" /> : d.ergebnis === 'nachzahlung' ? <Warning weight="fill" className="w-5 h-5 text-red-600" /> : <FileText weight="bold" className="w-5 h-5 text-slate-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{d.title}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(d.created_at).toLocaleDateString('de-DE')}
                    {d.saldo != null && (
                      <span className={`ml-2 font-mono ${d.ergebnis === 'guthaben' ? 'text-emerald-700' : d.ergebnis === 'nachzahlung' ? 'text-red-700' : 'text-slate-500'}`}>
                        {d.ergebnis === 'guthaben' ? `Guthaben ${fmtEur(d.saldo)}` : d.ergebnis === 'nachzahlung' ? `Nachzahlung ${fmtEur(-d.saldo)}` : 'Ausgeglichen'}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => download(d)}
                className="bg-slate-900 text-white px-3 py-2 hover:bg-slate-800 flex items-center gap-1 text-xs font-medium"
                data-testid={`download-doc-${d.id}`}
              >
                <Download weight="bold" className="w-4 h-4" />
                <span className="hidden sm:inline">PDF</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
